import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, errorText, imgSrc } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CoverField, ImagePicker } from "./ImageUpload";

const BLANK = { title: "", slug: "", category: "Insights", excerpt: "", cover_image: "", body: "", published: true };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="eyebrow !text-[#5C564E] !text-[10px]">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

export const PostEditor = ({ post, onDone }) => {
  const [form, setForm] = useState(post ? { ...BLANK, ...post } : BLANK);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    const payload = { title: form.title, slug: form.slug, category: form.category, excerpt: form.excerpt, cover_image: form.cover_image || "", body: form.body, published: form.published };
    try {
      if (post?.id) await http.put(`/admin/posts/${post.id}`, payload);
      else await http.post("/admin/posts", payload);
      toast.success(form.published ? "Article published" : "Draft saved");
      onDone();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="post-editor">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-display text-3xl text-[#221F1B]">{post?.id ? "Edit article" : "New article"}</h2>
        <div className="flex items-center gap-5">
          <label className="flex items-center gap-3 text-[14px] text-[#4A453E]">
            <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="post-published-switch" />
            {form.published ? "Published" : "Draft"}
          </label>
          <button onClick={onDone} className="text-[14px] text-[#5C564E] link-underline" data-testid="post-editor-cancel-button">Cancel</button>
          <button onClick={save} disabled={saving || form.title.trim().length < 3} className="btn-brass px-6 py-3 text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-50" data-testid="post-editor-save-button">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
        </div>
      </div>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2"><Field label="Title"><Input value={form.title} onChange={set("title")} className="bg-white text-lg h-12" data-testid="post-title-input" /></Field></div>
        <Field label="URL slug (optional)"><Input value={form.slug} onChange={set("slug")} placeholder="auto-generated from title" className="bg-white" data-testid="post-slug-input" /></Field>
        <Field label="Category"><Input value={form.category} onChange={set("category")} className="bg-white" data-testid="post-category-input" /></Field>
        <div className="md:col-span-2"><Field label="Excerpt"><Textarea rows={2} value={form.excerpt} onChange={set("excerpt")} className="bg-white" data-testid="post-excerpt-input" /></Field></div>
        <div className="md:col-span-2"><Field label="Cover image"><CoverField value={form.cover_image} onChange={(v) => setForm((f) => ({ ...f, cover_image: v }))} /></Field></div>
      </div>
      <Tabs defaultValue="write" className="mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
        <TabsList className="bg-[#EFE9DC]">
          <TabsTrigger value="write" data-testid="post-write-tab">Write</TabsTrigger>
          <TabsTrigger value="preview" data-testid="post-preview-tab">Preview</TabsTrigger>
        </TabsList>
        <ImagePicker label="Insert image in article" testId="post-insert-image-button" onPicked={(url) => setForm((f) => ({ ...f, body: `${f.body}${f.body ? "\n\n" : ""}![Image caption](${url})\n` }))} />
        </div>
        <TabsContent value="write">
          <Textarea rows={18} value={form.body} onChange={set("body")} className="bg-white font-mono text-[14px] leading-relaxed" placeholder="Write in Markdown: ## Heading, - list, > quote, **bold**" data-testid="post-body-input" />
        </TabsContent>
        <TabsContent value="preview">
          <div className="bg-white border border-[#D8CFBC] p-8 prose-ss min-h-[300px]" data-testid="post-preview"><ReactMarkdown components={{ img: ({ src, alt }) => <img src={imgSrc(src)} alt={alt || ""} className="w-full h-auto my-6" /> }}>{form.body || "*Nothing to preview yet.*"}</ReactMarkdown></div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
